<script setup>

import Checkbox from "@/Components/Checkbox.vue";
import AppLayout from "@/Layouts/AppLayout.vue";
import InputLabel from "@/Components/InputLabel.vue";
import TextInput from "@/Components/TextInput.vue";
import PaginationLinks from "@/Components/PaginationLinks.vue";
import { computed, ref, watch } from "vue";
import { Inertia } from "@inertiajs/inertia";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import DialogModal from "@/Components/DialogModal.vue";
import { Link, useForm, usePage } from "@inertiajs/inertia-vue3";
import { useHelpers, useTenantButtonVisibility, useTenantTextRules } from "@/helpers";
import ProjectNavigationButtons from "@/Components/Admin/Project/ProjectNavigationButtons.vue";
import ProjectIndexFilters from "@/Components/Admin/Project/ProjectIndexFilters.vue";
import ExportLoadingOverlay from '@/Components/ExportLoadingOverlay.vue'

const helpers = useHelpers();
const { isButtonVisible } = useTenantButtonVisibility();
const { resolveTextRule } = useTenantTextRules();
const page = usePage();

function openMailPreviewIfPresent() {
  const url = page.props.value?.project_mail_preview_url
    ?? page.props.value?.flash?.project_mail_preview_url;
  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
const props = defineProps({
  projects: Object,
  allProjectManagers: Array,
  projectManagers: Array,
  filters: Object,
  sorting: Object,
  type: String,
  availableYears: Object,
  companies: Object,
  vendors: Object,
  colorField: Object,
});

console.log('ProjectIndex.vue props:', props);

const currentUser = usePage().props.value.auth.user;

const isHandDelivered = (carrierId) => {
  return carrierId === 6;
}

const formatDate = (timestamp, format) => {
  if (!timestamp) {
    return '';
  }
  return dayjs.tz(timestamp * 1000).format(format);
}

// Empty / null / non-numeric qty (e.g. synced Pricing rows with blank garments) counts as 0.
const parseQuantity = (value) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const isShipped = (project) => {
  if (project.quantity_ordered <= 0) {
    return false;
  }
  let isNotShipped = false;
  let total = 0;
  let hasShippingFee = false;
  let hasOnClientCarrierAccount = false;
  project.styles.forEach(style => {
    let styleTotalShipped = 0;
    style.shipping_quantity.forEach(quantity => {
      if ((quantity?.shipping?.ship_to === 'Client' && quantity?.shipping?.ship_from !== 'Client') || quantity.received_at !== null) {
        const quantityShipped = parseQuantity(quantity.qty_ship);
        styleTotalShipped += quantityShipped;
        total += quantityShipped;
      }
    });
    // Exact match only: positive or negative Left to Ship keeps the row open.
    if (parseQuantity(style.garments) !== styleTotalShipped) {
      isNotShipped = true;
    }
  });
  if (isNotShipped === true) {
    return false;
  }
  project.shipping.forEach(shipping => {
    if (shipping.shipping_fee > 0) {
      hasShippingFee = true;
    }
    if (shipping.shiponclient) {
      hasOnClientCarrierAccount = true;
    }
  });

  return (total === parseQuantity(project.quantity_ordered)) && (hasShippingFee || hasOnClientCarrierAccount);
}
const isPurchaseOrderReady = (project) => {
  return isShipped(project) && (project.purchase_order?.pt_invoice || false)
}

// Color-only check: any style with Left to Ship != 0 (leftover or overship).
// Uses the same Client/received shipping filter as isShipped / shipping tab.
const hasOutstandingLeftToShip = (project) => {
  const styles = project.styles || [];
  for (const style of styles) {
    let styleTotalShipped = 0;
    (style.shipping_quantity || []).forEach(quantity => {
      if ((quantity?.shipping?.ship_to === 'Client' && quantity?.shipping?.ship_from !== 'Client') || quantity.received_at !== null) {
        styleTotalShipped += parseQuantity(quantity.qty_ship);
      }
    });
    if (parseQuantity(style.garments) !== styleTotalShipped) {
      return true;
    }
  }
  return false;
};

const purchaseOrderDueDay = (project) => {
  const raw = project.purchase_order?.purchaseduedate;
  if (raw === null || raw === undefined || raw === '') {
    return null;
  }
  let due;
  if (typeof raw === 'number') {
    due = dayjs.tz(raw * 1000);
  } else if (typeof raw === 'string' && /^\d+(\.\d+)?$/.test(raw.trim())) {
    due = dayjs.tz(Number(raw) * 1000);
  } else {
    due = dayjs.tz(raw);
  }
  return due.isValid() ? due.startOf('day') : null;
};

const purchaseOrderRowClass = (project) => {
  if (!hasOutstandingLeftToShip(project)) {
    return { 'bg-green-400': true, 'hover:bg-green-500': true };
  }
  const dueDay = purchaseOrderDueDay(project);
  if (!dueDay) {
    return { 'hover:bg-neutral-200': true };
  }
  const today = dayjs.tz().startOf('day');
  const daysUntilDue = dueDay.diff(today, 'day');
  if (daysUntilDue < 0) {
    return { 'bg-red-300': true, 'hover:bg-red-400': true };
  }
  if (daysUntilDue <= 21) {
    return { 'bg-yellow-200': true, 'hover:bg-yellow-300': true };
  }
  return { 'hover:bg-neutral-200': true };
};

const sendingProjectEmail = ref({ project: null, error: null, sending: false });
const sendingProjectEmailForm = useForm({
  email: null,
  subject: null,
});
const openProjectEmailModal = (project) => {
  sendingProjectEmail.value.project = project;
}
const closeProjectEmailModal = () => {
  sendingProjectEmail.value = { project: null, error: null, sending: false };
  sendingProjectEmailForm.reset();
}

const sendProjectEmail = () => {
  if (!sendingProjectEmailForm.email) {
    sendingProjectEmail.value.error = 'The recipient field is mandatory';
    return false;
  }
  if (!sendingProjectEmailForm.subject) {
    sendingProjectEmail.value.error = 'The subject field is mandatory';
    return false;
  }
  sendingProjectEmail.value.sending = true;

  sendingProjectEmailForm.post(route('admin.projects.send-email', { project: sendingProjectEmail.value.project.pid }), {
    preserveScroll: true,
    onSuccess: () => {
      sendingProjectEmailForm.reset();
      closeProjectEmailModal();
    }
  });
}

const resetProjectEmailError = () => {
  sendingProjectEmail.value.error = null;
}

const closingProject = ref({ project: null, error: null, sending: false });
const closingProjectForm = useForm({});

const openClosingProjectModal = (project) => {
  closingProject.value.project = project;
}
const closeClosingProjectModal = () => {
  closingProject.value = { project: null, error: null, sending: false };
  closingProjectForm.reset();
}

const closeProject = () => {
  closingProject.value.sending = true;

  closingProjectForm.post(route('admin.projects.close', { project: closingProject.value.project.pid }), {
    preserveScroll: true,
    onSuccess: () => {
      closingProjectForm.reset();
      closeClosingProjectModal();
      openMailPreviewIfPresent();
    }
  });
}

const csrVsrSelectAll = ref(false);
watch(csrVsrSelectAll, (value) => {
  if (value === false) {
    csrVsrForm.csr = [];
    csrVsrForm.vsr = [];
  } else {
    props.projects.data.forEach(project => {
      csrVsrForm.csr.push(project.pid);
      csrVsrForm.vsr.push(project.pid);
    });
  }
});
const csrVsrForm = useForm({
  csr: [],
  vsr: []
});

const sendingCsrVsr = ref(false);
const sendCsrVsr = () => {
  sendingCsrVsr.value = true;
  csrVsrForm.post(route('admin.projects.csr-vsr'), {
    preserveScroll: true,
    onFinish() {
      sendingCsrVsr.value = false;
    }
  })
};

const exportError = ref(null);

const closeExportError = () => {
  exportError.value = null;
};

const downloadExport = () => {
  exporting.value = true;
  console.log(props.type);
  let routeName = 'admin.projects.export';
  if (props.type === 'purchase-order') {
    routeName = 'admin.purchase-orders.export';
  } else if (props.type === 'closed-purchase-order') {
    routeName = 'admin.purchase-orders.export.closed';
  } else if (props.type === 'closed-project') {
    routeName = 'admin.purchase-orders.export.closed';
  }

  axios({
    url: route(routeName, route().params),
    method: 'POST',
    responseType: 'blob',
    validateStatus: () => true,
    headers: {
      'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  })
    .then(async (response) => {
      if (response.status !== 200) {
        try {
          const text = await response.data.text();
          let msg = 'The file is too large or an error occurred while generating the export.';

          try {
            const data = JSON.parse(text);
            msg = data?.message || msg;
          } catch (e) {
          }

          exportError.value = msg;
        } catch (e) {
          exportError.value =
            'The file is too large or an error occurred while generating the export.';
        } finally {
          exporting.value = false;
        }
        return;
      }

      const href = window.URL.createObjectURL(response.data);

      const anchorElement = document.createElement('a');
      anchorElement.href = href;

      const disposition = response.headers['content-disposition'] || '';
      let filename = 'export.xlsx';

      const matchFilename = disposition.match(/filename="?([^";]+)"?/);
      if (matchFilename && matchFilename[1]) {
        filename = matchFilename[1];
      }

      anchorElement.download = filename;
      document.body.appendChild(anchorElement);
      anchorElement.click();

      setTimeout(function () {
        document.body.removeChild(anchorElement);
        window.URL.revokeObjectURL(href);
      }, 200);

      exporting.value = false;
    })
    .catch((error) => {
      console.log(error);
      exportError.value =
        'An error occurred while generating the export. Please try again or apply more specific filters.';
      exporting.value = false;
    });
};

const reOpenProject = (project) => {
  const routeName = props.type === 'closed-purchase-order'
    ? 'admin.purchase-orders.closed.open'
    : 'admin.projects.closed.open';

  Inertia.post(route(routeName, { project: project.pid }), {}, {
    preserveScroll: true
  });
}

const trashProject = (project) => {
  Inertia.post(route('admin.projects.closed.trash', { project: project.pid }), {}, {
    preserveScroll: true
  });
}

const creatingProjectFromPurchaseOrder = ref({ project: null, sending: false });
const projectFromPurchaseOrderForm = useForm({
  projectname: null
});
const openCreateNewProjectModal = (project) => {
  creatingProjectFromPurchaseOrder.value.project = project;
}
const closeCreatingProjectFromPurchaseOrderModel = () => {
  creatingProjectFromPurchaseOrder.value = { project: null, error: null, sending: false };
  projectFromPurchaseOrderForm.reset();
}

const createNewProjectFromPurchaseOrder = () => {
  creatingProjectFromPurchaseOrder.value.sending = true;
  projectFromPurchaseOrderForm.post(
    route('admin.projects.from-purchase-order', { project: creatingProjectFromPurchaseOrder.value.project.pid }),
    {
      preserveScroll: true,
      onSuccess() {
        closeCreatingProjectFromPurchaseOrderModel();
        projectFromPurchaseOrderForm.reset();
      }
    }
  );
}

let defaultSorting = { column: 'pid', is_ascending: false };
if (props.type.includes('purchase-order')) {
  defaultSorting = { column: 'purchase-order-at', is_ascending: false };
}
const sorting = ref(Object.assign({}, defaultSorting, props.sorting));
const sortTable = (column) => {
  if (column === sorting.value.column) {
    sorting.value.is_ascending = !sorting.value.is_ascending;
  } else {
    sorting.value = { column, is_ascending: true };
  }
}
const sortingClass = (column) => {
  if (column !== sorting.value.column) {
    return ['fa', 'fa-sort'];
  }

  return sorting.value.is_ascending ? ['fa', 'fa-sort-down'] : ['fa', 'fa-sort-up'];
}

const preview = ref(null);
const projectIndexTitle = computed(() =>
  resolveTextRule('project.index.entity_plural', 'Projects', 'project.index.header')
);
const closedProjectsTitle = computed(() =>
  resolveTextRule('project.index.closed_entity_plural', 'Closed Projects', 'project.index.closed_header')
);
const purchaseOrdersTitle = computed(() =>
  resolveTextRule('project.index.purchase_orders', 'Purchase Orders', 'project.index.purchase_orders_header')
);
const closedPurchaseOrdersTitle = computed(() =>
  resolveTextRule('project.index.closed_purchase_orders', 'Closed Purchase Orders', 'project.index.closed_purchase_orders_header')
);
const purchaseOrderColumnLabel = computed(() =>
  resolveTextRule('project.index.table.purchase_order', 'Purchase Order', 'project.index.table.purchase_order')
);
const purchaseOrderDueDateColumnLabel = computed(() =>
  resolveTextRule('project.index.table.due_date', 'Due Date', 'project.index.table.due_date')
);
const reopenPurchaseOrderTitle = computed(() =>
  resolveTextRule('project.index.reopen_purchase_order', 'Re-open Purchase Order', 'project.index.reopen_purchase_order')
);
const projectNewButtonLabel = computed(() =>
  resolveTextRule('project.index.new_button', 'New Project', 'project.index.new_button')
);

const openPreviewModal = (image) => {
  preview.value = image;
}

const closePreviewModal = () => {
  preview.value = null;
}

const exporting = ref(false)
</script>

<template>
  <AppLayout title="Project Management">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        <span v-if="type === 'closed-purchase-order'">{{ closedPurchaseOrdersTitle }}</span>
        <span v-else-if="type === 'purchase-order'">{{ purchaseOrdersTitle }}</span>
        <span v-else-if="type === 'project'">{{ projectIndexTitle }}</span>
        <span v-else-if="type === 'closed-project'">{{ closedProjectsTitle }}</span>
        <span v-else>{{ projectIndexTitle }}</span>
      </h2>
    </template>

    <ExportLoadingOverlay v-model="exporting" title="Exporting…"
      message="Generating file, please wait. File may take a few moments to download. Click to dismiss."
      :autoCloseOnFocus="true" :closeOnUserInteraction="true" :interactionArmDelayMs="1000" />

    <div>
      <div class="mx-auto py-10 sm:px-6 lg:px-8">
        <div class="text-center">
          <ProjectNavigationButtons></ProjectNavigationButtons>
        </div>
        <div class="mt-5 mb-2 md:grid lg:grid-cols-6 md:grid-cols-2 grid-cols-1 gap-2">
          <Link
            :href="route('admin.projects.create')"
            class="w-full"
            v-if="helpers.userCanManageProject(currentUser) && isButtonVisible('project.index.create')"
          >
          <PrimaryButton type="button" class="w-full">{{ projectNewButtonLabel }}</PrimaryButton>
          </Link>
          <PrimaryButton
            v-if="isButtonVisible('project.index.export')"
            type="button"
            class="w-full"
            @click="downloadExport()"
          >
            Generate Spreadsheet
          </PrimaryButton>
          <PrimaryButton type="button"
            v-if="isButtonVisible('project.index.send_email') && helpers.userCanManageProject(currentUser) && (csrVsrForm.csr.length > 0 || csrVsrForm.vsr.length > 0)"
            @click="sendCsrVsr()" :disabled="sendingCsrVsr">
            Send Email
          </PrimaryButton>
        </div>
        <div class="mt-5 mb-5 md:grid lg:grid-cols-6 md:grid-cols-2 grid-cols-1 gap-2">
          <ProjectIndexFilters :project-managers="projectManagers" :all-project-managers="allProjectManagers"
            :type="type" :filters="filters" :sorting="sorting" :available-years="availableYears" :companies="companies"
            :vendors="vendors" :color-field="colorField"></ProjectIndexFilters>
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full w-full text-left text-sm font-light" v-if="type === 'project'">
            <thead class="border-b font-medium dark:border-neutral-500">
              <tr>
                <th scope="col" class="px-4 py-2" v-if="helpers.userCanManageProject(currentUser)">
                  <InputLabel for="select-all">
                    <div class="flex items-center">
                      <b>Select All</b>
                      <Checkbox id="select-all" name="select_all" class="ml-1" v-model:checked="csrVsrSelectAll"
                        :value="true" />
                    </div>
                  </InputLabel>
                </th>
                <th scope="col" class="px-4 py-2">Thumbnail</th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('company-name')">Company <span
                    :class="sortingClass('company-name')"></span></th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('project-manager')">Project Manager
                  <span :class="sortingClass('project-manager')"></span>
                </th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('project-name')">Project Name <span
                    :class="sortingClass('project-name')"></span></th>
                <th scope="col" class="px-4 py-2 cursor-pointer" v-if="(helpers.userCanManageProject(currentUser))"
                  @click="sortTable('estimated-profit')">
                  Estimated Profit <span :class="sortingClass('estimated-profit')"></span>
                </th>
                <th scope="col" class="px-4 py-2" v-if="(helpers.userCanManageProject(currentUser))">
                  Sample
                </th>
                <th scope="col" class="px-4 py-2">Tracking Number</th>
                <th scope="col" class="px-4 py-2">Target Delivery</th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('created-at')">Created At <span
                    :class="sortingClass('created-at')"></span></th>
                <th scope="col" class="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="project in projects.data" class="border-b transition duration-300 ease-in-out"
                :class="{ 'hover:bg-neutral-200': !isShipped(project), 'bg-green-400': isShipped(project), 'hover:bg-green-500': isShipped(project) }">
                <td class="px-4 py-2 break-words" v-if="helpers.userCanManageProject(currentUser)">
                  <InputLabel :for="`csr-${project.pid}`">
                    <div class="flex items-center">
                      CSR
                      <Checkbox :id="`csr-${project.pid}`" :name="`csr-${project.pid}`" class="ml-1"
                        v-model:checked="csrVsrForm.csr" :value="project.pid" />
                    </div>
                  </InputLabel>
                  <InputLabel :for="`vsr-${project.pid}`">
                    <div class="flex items-center">
                      VSR
                      <Checkbox :id="`vsr-${project.pid}`" :name="`vsr-${project.pid}`" class="ml-1"
                        v-model:checked="csrVsrForm.vsr" :value="project.pid" />
                    </div>
                  </InputLabel>
                </td>
                <td class="px-4 py-2 break-words">
                  <img v-if="project.media_thumbnail" class="w-16 cursor-pointer" :src="project.media_thumbnail.url"
                    @click="openPreviewModal(project.media_thumbnail)" :alt="project.media_thumbnail.file_name">
                </td>
                <td class="px-4 py-2 break-words" v-text="project.company.client"
                  :class="{ 'text-red-600': project.company.class === 'D' }"></td>
                <td class="px-4 py-2 break-words" v-text="project.manager?.full_name"></td>
                <td class="px-4 py-2 break-words" v-text="project.projectname"></td>
                <td class="px-4 py-2 break-words"
                  v-text="`$ ${parseFloat(project?.pricing?.prj_est_profit || 0).toFixed(2)}`"
                  v-if="(helpers.userCanManageProject(currentUser))"></td>
                <td class="px-4 py-2 break-words" v-text="project.bid_number"
                  v-if="(helpers.userCanManageProject(currentUser))"></td>
                <td class="px-4 py-2 break-words">
                  <div v-if="isHandDelivered(project?.last_shipping?.carrier_id)">
                    <span>Hand delivered</span>
                  </div>
                  <div v-else>
                    <a target="_blank"
                      v-if="project?.last_shipping?.tracking_number || project?.last_shipping?.last_tracking_number?.tracking_no"
                      :href="`${project?.last_shipping?.carrier?.weblink}${project?.last_shipping?.tracking_number || project?.last_shipping?.last_tracking_number?.tracking_no}`"
                      v-text="project?.last_shipping?.tracking_number || project?.last_shipping?.last_tracking_number?.tracking_no"></a>
                  </div>
                </td>
                <td class="px-4 py-2 break-words" v-text="(project?.milestone?.prdtntrgtdelvry || '')"></td>
                <td class="px-4 py-2 break-words" v-text="formatDate(project.created_date, 'MM/DD/YYYY')"></td>
                <td>
                  <button class="fa fa-envelope p-1" title="Mail Project"
                    @click="openProjectEmailModal(project)"></button>
                  <Link :href="route('admin.projects.edit', { project: project.pid })" class="fa fa-pen p-1"
                    :title="`Edit ${project.projectname}`">
                  </Link>
                  <button class="fa fa-times p-1" :title="`Close ${project.projectname}`"
                    v-if="(helpers.userCanManageProject(currentUser))"
                    @click="openClosingProjectModal(project)"></button>
                </td>
              </tr>
            </tbody>
          </table>

          <table class="min-w-full w-full text-left text-sm font-light" v-if="type === 'closed-project'">
            <thead class="border-b font-medium dark:border-neutral-500">
              <tr>
                <th scope="col" class="px-4 py-2">Thumbnail</th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('company-name')">Company <span
                    :class="sortingClass('company-name')"></span></th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('project-manager')">Project Manager
                  <span :class="sortingClass('project-manager')"></span>
                </th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('project-name')">Project Name <span
                    :class="sortingClass('project-name')"></span></th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('closed-date')">Closed Date <span
                    :class="sortingClass('closed-date')"></span></th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('project-quote')"
                  v-if="(helpers.userCanManageProject(currentUser))">Project Quote <span
                    :class="sortingClass('project-quote')"></span>
                </th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('order-placed')">Order Processed
                  <span :class="sortingClass('order-placed')"></span>
                </th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('estimated-unit-cost')"
                  v-if="(helpers.userCanManageProject(currentUser))">Estimated Unit Cost <span
                    :class="sortingClass('estimated-unit-cost')"></span>
                </th>
                <th scope="col" class="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="project in projects.data" class="border-b transition duration-300 ease-in-out">
                <td class="px-4 py-2 break-words">
                  <img v-if="project.media_thumbnail" class="w-16 cursor-pointer" :src="project.media_thumbnail.url"
                    @click="openPreviewModal(project.media_thumbnail)" :alt="project.media_thumbnail.file_name">
                </td>
                <td class="px-4 py-2 break-words" v-text="project?.company?.client"
                  :class="{ 'text-red-600': project?.company?.class === 'D' }"></td>
                <td class="px-4 py-2 break-words" v-text="project.manager?.full_name"></td>
                <td class="px-4 py-2 break-words" v-text="project.projectname"></td>
                <td class="px-4 py-2 break-words" v-text="formatDate(project.close_date, 'MM/DD/YYYY')"></td>
                <td class="px-4 py-2 break-words" v-text="`$ ${parseFloat(project?.pricing?.prjquote || 0).toFixed(2)}`"
                  v-if="(helpers.userCanManageProject(currentUser))"></td>
                <td class="px-4 py-2 break-words" v-text="project.order_placeon"></td>
                <td class="px-4 py-2 break-words"
                  v-text="`$ ${parseFloat(project?.pricing?.prj_estimatecost || 0).toFixed(2)}`"
                  v-if="(helpers.userCanManageProject(currentUser))"></td>
                <td>
                  <Link :href="route('admin.projects.closed.show', { project: project.pid })" class="fa p-1"
                    :class="helpers.userHasPermission(currentUser, 'edit_closed_purchase_order') ? 'fa-pen' : 'fa-eye'"
                    :title="helpers.userHasPermission(currentUser, 'edit_closed_purchase_order') ? `Edit ${project.projectname}` : `View ${project.projectname}`">
                  </Link>
                  <button class="fa fa-sync p-1" title="Re-open Project" @click="reOpenProject(project)"></button>
                  <button class="fa fa-trash p-1" :title="`Delete ${project.projectname}`"
                    @click.prevent="trashProject(project)" v-if="(helpers.userCanManageProject(currentUser))"></button>
                </td>
              </tr>
            </tbody>
          </table>

          <table class="min-w-full w-full text-left text-sm font-light" v-if="type === 'purchase-order'">
            <thead class="border-b font-medium dark:border-neutral-500">
              <tr>
                <th scope="col" class="px-4 py-2" v-if="helpers.userCanManageProject(currentUser)">
                  <InputLabel for="select-all">
                    <div class="flex items-center">
                      <b>Select All</b>
                      <Checkbox id="select-all" name="select_all" class="ml-1" v-model:checked="csrVsrSelectAll"
                        :value="true" />
                    </div>
                  </InputLabel>
                </th>
                <th scope="col" class="px-4 py-2">Thumbnail</th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('company-name')">Company <span
                    :class="sortingClass('company-name')"></span></th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('project-manager')">Project Manager
                  <span :class="sortingClass('project-manager')"></span>
                </th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('project-name')">Project Name <span
                    :class="sortingClass('project-name')"></span></th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('purchase-order')">{{ purchaseOrderColumnLabel }}
                  <span :class="sortingClass('purchase-order')"></span>
                </th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('pt-invoice')">PT Invoice <span
                    :class="sortingClass('pt-invoice')"></span></th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('due-date')">{{ purchaseOrderDueDateColumnLabel }} <span
                    :class="sortingClass('due-date')"></span></th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('project-quote')"
                  v-if="(helpers.userCanManageProject(currentUser))">Project Quote <span
                    :class="sortingClass('project-quote')"></span></th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('order-placed')">Order Processed
                  <span :class="sortingClass('order-placed')"></span>
                </th>
                <th scope="col" class="px-4 py-2">Tracking Number</th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('order-eta')">Order ETA <span
                    :class="sortingClass('order-eta')"></span></th>
                <th scope="col" class="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="project in projects.data" class="border-b transition duration-300 ease-in-out"
                :class="purchaseOrderRowClass(project)">
                <td class="px-4 py-2 break-words" v-if="helpers.userCanManageProject(currentUser)">
                  <InputLabel :for="`csr-${project.pid}`">
                    <div class="flex items-center">
                      CSR
                      <Checkbox :id="`csr-${project.pid}`" :name="`csr-${project.pid}`" class="ml-1"
                        v-model:checked="csrVsrForm.csr" :value="project.pid" />
                    </div>
                  </InputLabel>
                  <InputLabel :for="`vsr-${project.pid}`">
                    <div class="flex items-center">
                      VSR
                      <Checkbox :id="`vsr-${project.pid}`" :name="`vsr-${project.pid}`" class="ml-1"
                        v-model:checked="csrVsrForm.vsr" :value="project.pid" />
                    </div>
                  </InputLabel>
                </td>
                <td class="px-4 py-2 break-words">
                  <img v-if="project.media_thumbnail" class="w-16 cursor-pointer" :src="project.media_thumbnail.url"
                    @click="openPreviewModal(project.media_thumbnail)" :alt="project.media_thumbnail.file_name">
                </td>
                <td class="px-4 py-2 break-words" v-text="project.company.client"
                  :class="{ 'text-red-600': project.company.class === 'D' }"></td>
                <td class="px-4 py-2 break-words" v-text="project.manager?.full_name"></td>
                <td class="px-4 py-2 break-words" v-text="project.projectname"></td>
                <td class="px-4 py-2 break-words" v-text="project.purchase_order.purchaseorder"></td>
                <td class="px-4 py-2 break-words" v-text="project.purchase_order.pt_invoice"></td>
                <td class="px-4 py-2 break-words"
                  v-text="formatDate(project.purchase_order.purchaseduedate, 'MM/DD/YYYY')">
                </td>
                <td class="px-4 py-2 break-words" v-text="`$ ${parseFloat(project?.pricing?.prjquote || 0).toFixed(2)}`"
                  v-if="(helpers.userCanManageProject(currentUser))"></td>
                <td class="px-4 py-2 break-words" v-text="project.order_placeon"></td>
                <td class="px-4 py-2 break-words">
                  <div v-if="isHandDelivered(project?.last_shipping?.carrier_id)">
                    <span>Hand delivered</span>
                  </div>
                  <div v-else>
                    <a target="_blank"
                      v-if="project?.last_shipping?.tracking_number || project?.last_shipping?.last_tracking_number?.tracking_no"
                      :href="`${project?.last_shipping?.carrier?.weblink}${project?.last_shipping?.tracking_number || project?.last_shipping?.last_tracking_number?.tracking_no}`"
                      v-text="project?.last_shipping?.tracking_number || project?.last_shipping?.last_tracking_number?.tracking_no"></a>
                  </div>
                </td>
                <td class="px-4 py-2 break-words" v-text="project.order_eta_on"></td>
                <td>
                  <button class=" p-1" style="color: #ed1a02;" :title="`Close ${project.projectname}`"
                    v-if="(helpers.userIsAdmin(currentUser))" @click="openClosingProjectModal(project)"> <img
                      class="w-4" src="/content/images/stopSign.png" alt="stop sign"> </button>
                  <button class="fa fa-envelope p-1" title="Mail Project"
                    @click="openProjectEmailModal(project)"></button>
                  <Link :href="route('admin.projects.edit', { project: project.pid })" class="fa fa-pen p-1"
                    :title="`Edit ${project.projectname}`">
                  </Link>
                  <button class="fa fa-times p-1" :title="`Close ${project.projectname}`"
                    v-if="(helpers.userCanManageProject(currentUser) && isPurchaseOrderReady(project))"
                    @click="openClosingProjectModal(project)"></button>
                </td>
              </tr>
            </tbody>
          </table>

          <table class="min-w-full w-full text-left text-sm font-light" v-if="type === 'closed-purchase-order'">
            <thead class="border-b font-medium dark:border-neutral-500">
              <tr>
                <th scope="col" class="px-4 py-2">Thumbnail</th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('company-name')">Company <span
                    :class="sortingClass('company-name')"></span></th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('project-manager')">Project Manager
                  <span :class="sortingClass('project-manager')"></span>
                </th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('project-name')">Project Name <span
                    :class="sortingClass('project-name')"></span></th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('closed-date')">Closed Date <span
                    :class="sortingClass('closed-date')"></span></th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('purchase-order')">{{ purchaseOrderColumnLabel }}
                  <span :class="sortingClass('purchase-order')"></span>
                </th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('due-date')">{{ purchaseOrderDueDateColumnLabel }} <span
                    :class="sortingClass('due-date')"></span></th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('project-quote')"
                  v-if="(helpers.userCanManageProject(currentUser))">Project Quote <span
                    :class="sortingClass('project-quote')"></span></th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('order-placed')">Order Processed
                  <span :class="sortingClass('order-placed')"></span>
                </th>
                <th scope="col" class="px-4 py-2 cursor-pointer" @click="sortTable('estimated-unit-cost')"
                  v-if="(helpers.userCanManageProject(currentUser))">Estimated Unit Cost <span
                    :class="sortingClass('estimated-unit-cost')"></span>
                </th>
                <th scope="col" class="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="project in projects.data"
                class="border-b transition duration-300 ease-in-out hover:gb-neutral-200">
                <td class="px-4 py-2 break-words">
                  <img v-if="project.media_thumbnail" class="w-16 cursor-pointer" :src="project.media_thumbnail.url"
                    @click="openPreviewModal(project.media_thumbnail)" :alt="project.media_thumbnail.file_name">
                </td>
                <td class="px-4 py-2 break-words" v-text="project.company.client"
                  :class="{ 'text-red-600': project.company.class === 'D' }"></td>
                <td class="px-4 py-2 break-words" v-text="project.manager?.full_name"></td>
                <td class="px-4 py-2 break-words" v-text="project.projectname"></td>
                <td class="px-4 py-2 break-words" v-text="formatDate(project.close_date, 'MM/DD/YYYY')"></td>
                <td class="px-4 py-2 break-words" v-text="project.purchase_order.purchaseorder"></td>
                <td class="px-4 py-2 break-words"
                  v-text="formatDate(project.purchase_order.purchaseduedate, 'MM/DD/YYYY')">
                </td>
                <td class="px-4 py-2 break-words" v-text="`$ ${parseFloat(project?.pricing?.prjquote || 0).toFixed(2)}`"
                  v-if="(helpers.userCanManageProject(currentUser))"></td>
                <td class="px-4 py-2 break-words" v-text="project.order_placeon"></td>
                <td class="px-4 py-2 break-words"
                  v-text="`$ ${parseFloat(project?.pricing?.prj_estimatecost || 0).toFixed(2)}`"
                  v-if="(helpers.userCanManageProject(currentUser))"></td>
                <td>
                  <Link :href="route('admin.projects.closed.show', { project: project.pid })" class="fa p-1"
                    :class="helpers.userHasPermission(currentUser, 'edit_closed_purchase_order') ? 'fa-pen' : 'fa-eye'"
                    :title="helpers.userHasPermission(currentUser, 'edit_closed_purchase_order') ? `Edit ${project.projectname}` : `View ${project.projectname}`">
                  </Link>
                  <button class="fa fa-sync p-1" :title="reopenPurchaseOrderTitle" @click="reOpenProject(project)"></button>
                  <button class="fa fa-plus p-1" :title="`Create New Project From ${project.projectname}`"
                    @click.prevent="openCreateNewProjectModal(project)"
                    v-if="(helpers.userCanManageProject(currentUser))"></button>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="text-center mt-5">
            <PaginationLinks :pagination="projects" />
          </div>
        </div>

      </div>
    </div>

    <DialogModal :show="sendingProjectEmail.project !== null" @close="closeProjectEmailModal">
      <template #title>
        Sending email for project <b v-text="sendingProjectEmail.project.projectname"></b>
      </template>

      <template #content>
        <p v-if="sendingProjectEmail.error" v-text="sendingProjectEmail.error" class="text-red-600"></p>
        <div class="mt-2">
          <InputLabel for="recipient" value="Recipient" :required="true" />
          <TextInput id="recipient" type="email" :disabled="sendingProjectEmail.sending" class="mt-1 w-full"
            placeholder="mail@example.com" v-model="sendingProjectEmailForm.email" @input="resetProjectEmailError" />
        </div>
        <div class="mt-2">
          <InputLabel for="subject" value="Subject" :required="true" />
          <TextInput id="subject" type="text" :disabled="sendingProjectEmail.sending" class="mt-1 w-full" placeholder=""
            v-model="sendingProjectEmailForm.subject" @input="resetProjectEmailError" />
        </div>
      </template>

      <template #footer>
        <SecondaryButton @click="closeProjectEmailModal" :disabled="sendingProjectEmail.sending" class="mr-2">
          Cancel
        </SecondaryButton>
        <PrimaryButton @click="sendProjectEmail" :disabled="sendingProjectEmail.sending">
          Send
        </PrimaryButton>
      </template>
    </DialogModal>

    <DialogModal :show="closingProject.project !== null" @close="closeClosingProjectModal">
      <template #title>
        Closing project <b v-text="closingProject.project.projectname"></b>
      </template>

      <template #content>
        You are about to close the project <b v-text="closingProject.project.projectname"></b>. Are you sure you would
        like to proceed?
      </template>

      <template #footer>
        <SecondaryButton @click="closeClosingProjectModal" :disabled="closingProject.sending" class="mr-2">
          Cancel
        </SecondaryButton>
        <PrimaryButton @click="closeProject" :disabled="closingProject.sending">
          Close
        </PrimaryButton>
      </template>
    </DialogModal>

    <DialogModal :show="creatingProjectFromPurchaseOrder.project !== null"
      @close="closeCreatingProjectFromPurchaseOrderModel">
      <template #title>
        Cloning Project <b v-text="creatingProjectFromPurchaseOrder.project.projectname"></b>
      </template>

      <template #content>
        <p v-if="creatingProjectFromPurchaseOrder.error" v-text="creatingProjectFromPurchaseOrder.error"
          class="text-red-600"></p>
        <div class="mt-2">
          <InputLabel for="project-name" value="Project Name" :required="true" />
          <TextInput id="project-name" type="text" :disabled="creatingProjectFromPurchaseOrder.sending"
            class="mt-1 w-full" v-model="projectFromPurchaseOrderForm.projectname" />
        </div>
      </template>

      <template #footer>
        <SecondaryButton @click="closeCreatingProjectFromPurchaseOrderModel"
          :disabled="creatingProjectFromPurchaseOrder.sending" class="mr-2">
          Cancel
        </SecondaryButton>
        <PrimaryButton @click="createNewProjectFromPurchaseOrder" :disabled="creatingProjectFromPurchaseOrder.sending">
          Create
        </PrimaryButton>
      </template>
    </DialogModal>
    <DialogModal :show="preview !== null" @close="closePreviewModal" max-width="7xl">
      <template #title>
        <b v-text="preview.file_name"></b>
      </template>

      <template #content>
        <img :src="preview.url" :alt="preview.file_name">
      </template>
    </DialogModal>

    <DialogModal :show="exportError !== null" @close="closeExportError">
      <template #title>
        Export too large
      </template>

      <template #content>
        <p class="text-red-600">
          {{ exportError }}
        </p>
        <p class="mt-2">
          Try applying more specific filters (by date, company, vendor, year, etc.) to reduce the amount of data being
          exported.
        </p>
      </template>

      <template #footer>
        <PrimaryButton @click="closeExportError">
          OK
        </PrimaryButton>
      </template>
    </DialogModal>
  </AppLayout>
</template>
